import { UiLabel } from 'hx-services';
import { EditorCountry } from '@callcenter-app/service/order-editor.service';
import { uiLabelText } from '@callcenter-app/util/ui-label';

/**
 * The phone field of the new editor: a flag with the dial code («+7»), a divider and the digits the operator types («700 000 01 01»).
 * The countries come from E1 as the country rows store them — «+7 7» + 9 digits for Казахстан, «+7 » + 10 for Россия, «+996 » + 9,
 * «+998» + 9 — and are turned here into what the field needs: the dial code that is printed, the number of digits that are TYPED
 * (the second «7» of Казахстан is typed, it is the first digit of the operator's code), the grouping, the flag. Whatever the
 * backend normalises («7» + 10 digits for the same country) gives the same result: the length of a whole number is the digits of the
 * prefix plus the digits after it. Pure.
 */

export type FlagCode = 'kz' | 'ru' | 'kg' | 'uz' | 'tj' | 'other';

export interface PhoneCountry {
  id: number;
  title: UiLabel;
  /** The code the flag button prints: «+7». */
  dialCode: string;
  /** Its digits: «7». */
  dialDigits: string;
  /** How many digits the operator types after the dial code: 10 for a «+7» country, 9 for «+996». */
  nationalLength: number;
  /** The grouping of the typed digits: [3, 3, 2, 2] — «700 000 01 01». */
  groups: number[];
  flag: FlagCode;
}

const KNOWN_GROUPS: Record<string, number[]> = {
  '7': [3, 3, 2, 2],
  '998': [2, 3, 2, 2],
  '996': [3, 3, 3],
  '992': [3, 3, 3],
};

/** A phone is digits only on the wire: the dial code and the national part — «77000000101». */
export function fullPhone(national: string, country: Pick<PhoneCountry, 'dialDigits'>): string {
  return country.dialDigits + national;
}

export function toPhoneCountry(country: EditorCountry): PhoneCountry {
  const prefix = country.phonePrefix.trim();
  const prefixDigits = prefix.replace(/\D/g, '');
  const dialToken = prefix.split(/\s+/)[0] ?? '';
  const dialDigits = dialToken.replace(/\D/g, '') || prefixDigits;
  const nationalLength = Math.max(1, prefixDigits.length + country.phoneLength - dialDigits.length);
  return {
    id: country.id,
    title: country.title,
    dialCode: '+' + dialDigits,
    dialDigits,
    nationalLength,
    groups: groupSizes(dialDigits, nationalLength),
    flag: flagOf(country, prefixDigits),
  };
}

function groupSizes(dialDigits: string, nationalLength: number): number[] {
  const known = KNOWN_GROUPS[dialDigits];
  if (known && known.reduce((a, b) => a + b, 0) === nationalLength) {
    return known;
  }
  const sizes: number[] = [];
  let left = nationalLength;
  while (left > 0) {
    const size = left === 4 ? 2 : Math.min(3, left);
    sizes.push(size);
    left -= size;
  }
  return sizes;
}

const FLAG_BY_TITLE: ReadonlyArray<{ flag: FlagCode; words: readonly string[] }> = [
  { flag: 'kz', words: ['казахстан', 'қазақстан', 'kazakhstan'] },
  { flag: 'ru', words: ['россия', 'ресей', 'russia'] },
  { flag: 'kg', words: ['кыргызстан', 'қырғызстан', 'kyrgyzstan'] },
  { flag: 'uz', words: ['узбекистан', 'өзбекстан', 'uzbekistan'] },
  { flag: 'tj', words: ['таджикистан', 'тәжікстан', 'tajikistan'] },
];

const FLAG_BY_CODE: Record<string, FlagCode> = { kz: 'kz', ru: 'ru', kg: 'kg', uz: 'uz', tj: 'tj' };

const FLAG_BY_PREFIX: Record<string, FlagCode> = { '77': 'kz', '7': 'ru', '996': 'kg', '998': 'uz', '992': 'tj' };

/**
 * The flag of a country: its two-letter code when the backend sends one, else the name (a title in any of the languages), else the shape
 * of the prefix («+7 7» is Казахстан, a bare «+7» Россия). Anything else gets the neutral flag.
 */
export function flagOf(country: Pick<EditorCountry, 'title' | 'code'>, prefixDigits: string): FlagCode {
  const byCode = FLAG_BY_CODE[(country.code ?? '').trim().toLowerCase()];
  if (byCode) {
    return byCode;
  }
  const titles = Object.values(country.title).filter((t): t is string => typeof t === 'string').map(t => t.toLowerCase());
  const byTitle = FLAG_BY_TITLE.find(f => f.words.some(word => titles.some(t => t.includes(word))));
  return byTitle?.flag ?? FLAG_BY_PREFIX[prefixDigits] ?? 'other';
}

/**
 * What the field keeps of whatever was typed or pasted: digits only; a number pasted WITH its dial code («+7 700 000 01 01», «8 700…», «7700…»)
 * loses the code — «8» is the trunk prefix of the «+7» countries — and the rest is cut to the length of a national number. While the
 * operator is still typing (no more digits than a number has) nothing is stripped: a «7» typed first is the first digit of the operator's code.
 */
export function sanitizeNational(raw: string, country: Pick<PhoneCountry, 'dialDigits' | 'nationalLength'>): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length > country.nationalLength) {
    const codes = country.dialDigits === '7' ? ['7', '8'] : [country.dialDigits];
    const code = codes.find(c => digits.startsWith(c));
    if (code) {
      digits = digits.slice(code.length);
    }
  }
  return digits.slice(0, country.nationalLength);
}

/** «7000000101» → «700 000 01 01»; a half-typed number is grouped as far as it goes, with no trailing space. */
export function formatNational(national: string, country: Pick<PhoneCountry, 'groups'>): string {
  const parts: string[] = [];
  let pos = 0;
  for (const size of country.groups) {
    if (pos >= national.length) {
      break;
    }
    parts.push(national.slice(pos, pos + size));
    pos += size;
  }
  return parts.join(' ');
}

export function isCompleteNational(national: string, country: Pick<PhoneCountry, 'nationalLength'>): boolean {
  return national.length === country.nationalLength;
}

/**
 * The caret after the field's text was rewritten with its spaces: the operator's caret stood after `digitsBefore` digits, and stands after
 * the same digits in the formatted text — editing a number in the middle does not throw the caret to the end.
 */
export function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) {
    return 0;
  }
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted.charAt(i))) {
      seen++;
      if (seen === digitsBefore) {
        return i + 1;
      }
    }
  }
  return formatted.length;
}

/** The national part of a number the server sent («77000000101» → «7000000101»), or the input as it is when it does not start with the dial code. */
export function nationalOf(phone: string | undefined, country: Pick<PhoneCountry, 'dialDigits' | 'nationalLength'>): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits.startsWith(country.dialDigits) && digits.length > country.nationalLength
    ? digits.slice(country.dialDigits.length, country.dialDigits.length + country.nationalLength)
    : digits.slice(0, country.nationalLength);
}

/** A scope with no countries at all (a broken configuration) still gets a field to type a number into: «+7», ten digits. */
export const FALLBACK_COUNTRY: PhoneCountry = {
  id: 0,
  title: { ru: 'Казахстан', kk: 'Қазақстан', en: 'Kazakhstan' },
  dialCode: '+7',
  dialDigits: '7',
  nationalLength: 10,
  groups: [3, 3, 2, 2],
  flag: 'kz',
};

/** The country the number was typed in, else the country of the operator's city, else the first one the scope has. */
export function pickCountry(countries: readonly PhoneCountry[], countryId: number | undefined, cityCountryId?: number): PhoneCountry {
  return countries.find(c => c.id === countryId) ?? countries.find(c => c.id === cityCountryId) ?? countries[0] ?? FALLBACK_COUNTRY;
}

/** «Казахстан +7» of a country chip. */
export function countryChipText(country: Pick<PhoneCountry, 'title' | 'dialCode'>, lang: string): string {
  return `${uiLabelText(country.title, lang) ?? ''} ${country.dialCode}`.trim();
}
